import * as React from 'react';
import { useUser } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateBody,
  EmptyStateFooter,
  PageSection,
  Spinner,
} from '@patternfly/react-core';
import AccessDenied from './AccessDenied';
import { parseAdminAccess } from 'src/contracts';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
const ADMIN_ACCESS_URL = `${API_BASE}/api/auth/admin-access`;

type AdminAccessState = 'loading' | 'allowed' | 'denied' | 'error';

const AccessCheckFailed = (): React.ReactElement => (
  <PageSection hasBodyWrapper={false} isFilled>
    <EmptyState titleText="Access check failed" variant="full">
      <EmptyStateBody>
        The dashboard could not verify your admin access. Refresh and try again.
      </EmptyStateBody>
      <EmptyStateFooter>
        <Button variant="primary" onClick={() => window.location.reload()}>Retry</Button>
      </EmptyStateFooter>
    </EmptyState>
  </PageSection>
);

const AdminGate = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const { isLoaded, isSignedIn } = useUser();
  const authedFetch = useAuthedFetch();
  const [adminAccessState, setAdminAccessState] = React.useState<AdminAccessState>('loading');

  React.useEffect(() => {
    let canceled = false;

    if (!isLoaded) {
      setAdminAccessState('loading');
      return () => {
        canceled = true;
      };
    }

    if (!isSignedIn) {
      setAdminAccessState('loading');
      return () => {
        canceled = true;
      };
    }

    const loadAdminAccess = async () => {
      setAdminAccessState('loading');
      const response = await authedFetch(ADMIN_ACCESS_URL);
      if (!response.ok) {
        if (canceled) {
          return;
        }
        setAdminAccessState('error');
        return;
      }

      const rawResponse = await response.json();
      if (canceled) {
        return;
      }

      const parsedResponse = parseAdminAccess(rawResponse);
      if (!parsedResponse.ok) {
        setAdminAccessState('error');
        return;
      }

      const isAdmin = parsedResponse.value.is_admin;
      const nextState = isAdmin ? 'allowed' : 'denied';
      setAdminAccessState(nextState);
    };

    loadAdminAccess().catch(() => {
      if (canceled) {
        return;
      }
      setAdminAccessState('error');
    });

    return () => {
      canceled = true;
    };
  }, [authedFetch, isLoaded, isSignedIn]);

  const isCheckingAdminAccess = Boolean(isSignedIn) && adminAccessState === 'loading';
  const shouldShowLoading = !isLoaded || isCheckingAdminAccess;
  if (shouldShowLoading) {
    return (
      <Bullseye>
        <Spinner />
      </Bullseye>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (adminAccessState === 'denied') {
    return <AccessDenied />;
  }

  if (adminAccessState === 'error') {
    return <AccessCheckFailed />;
  }

  return <>{children}</>;
};

export default AdminGate;
